import { Link } from "react-router-dom";
import { Home, ArrowLeft, Search, HelpCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { BRAND_NAME, getSupportEmail } from "@/config/appConfig";

export default function NotFoundPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const supportEmail = getSupportEmail();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="relative">
                <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-violet-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-base sm:text-lg">S</span>
                </div>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                {BRAND_NAME}
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-gray-600 hover:text-violet-600 transition-colors">
                Home
              </Link>
              <Link to="/demo" className="text-gray-600 hover:text-violet-600 transition-colors">
                Demo
              </Link>
              <Link to="/pricing" className="text-gray-600 hover:text-violet-600 transition-colors">
                Pricing
              </Link>
            </nav>
            
            {/* Auth Buttons - Desktop */}
            <div className="hidden sm:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700">Get Started</Button>
              </Link>
            </div>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t py-4 space-y-3">
              <Link to="/" className="block px-3 py-2 text-gray-600 hover:text-violet-600">Home</Link>
              <Link to="/demo" className="block px-3 py-2 text-gray-600 hover:text-violet-600">Demo</Link>
              <Link to="/pricing" className="block px-3 py-2 text-gray-600 hover:text-violet-600">Pricing</Link>
              <div className="flex gap-2 px-3 pt-2">
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full" size="sm">Sign In</Button>
                </Link>
                <Link to="/signup" className="flex-1">
                  <Button className="w-full bg-violet-600 hover:bg-violet-700" size="sm">Get Started</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 sm:py-20">
        <div className="text-center max-w-lg">
          {/* 404 Graphic */}
          <div className="mb-6 sm:mb-8">
            <div className="relative inline-block">
              <div className="text-[100px] sm:text-[150px] font-bold text-gray-100 leading-none select-none">404</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <Search className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Message */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            Page Not Found
          </h1>
          <p className="text-gray-600 mb-6 sm:mb-8 text-base sm:text-lg px-4">
            Oops! The page you're looking for doesn't exist or has been moved.
            Let's get you back on track.
          </p>
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/" className="w-full sm:w-auto">
              <Button size="default" className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700 gap-2">
                <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                Go to Homepage
              </Button>
            </Link>
            <Button 
              size="default" 
              variant="outline" 
              onClick={() => window.history.back()}
              className="gap-2 w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              Go Back
            </Button>
          </div>
          
          {/* Help Links */}
          <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200 mx-4">
            <p className="text-gray-500 text-sm mb-3 sm:mb-4">Need help?</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-sm">
              <Link to="/demo" className="text-violet-600 hover:text-violet-700 flex items-center justify-center gap-1">
                <HelpCircle className="w-4 h-4" />
                View Demo
              </Link>
              <a href={`mailto:${supportEmail}`} className="text-violet-600 hover:text-violet-700">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">S</span>
              </div>
              <span className="text-lg font-bold text-gray-900">{BRAND_NAME}</span>
            </Link>
            
            {/* Links */}
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <Link to="/terms" className="hover:text-violet-600">Terms</Link>
              <Link to="/privacy" className="hover:text-violet-600">Privacy</Link>
              <a href={`mailto:${supportEmail}`} className="hover:text-violet-600">Support</a>
            </div>
            
            {/* Copyright */}
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
